import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Minus, Plus, Pencil, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { MODULE_REGISTRY } from './moduleRegistry';
import { useDashboardLayout } from './useDashboardLayout';

const LONG_PRESS_MS = 400;

function ModuleCardBody({ module, badge }) {
  const Icon = module.icon;
  return (
    <>
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-ga-sm bg-ga-primary-soft">
        <Icon className="h-5 w-5 text-ga-primary" aria-hidden="true" />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[1.1rem] rounded-ga-full bg-ga-danger px-1 text-center text-[10px] font-bold leading-[1.1rem] text-ga-text-on-primary">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <span className="min-w-0 truncate text-sm font-medium text-ga-text-primary">
        {module.title}
      </span>
    </>
  );
}

function SortableModuleCard({ module, editing, badge, onHide, onNavigate, onLongPress }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: module.id,
    disabled: !editing,
  });
  const pressTimer = useRef(null);

  const startPress = () => {
    if (editing) return;
    pressTimer.current = setTimeout(onLongPress, LONG_PRESS_MS);
  };
  const cancelPress = () => clearTimeout(pressTimer.current);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className={`module-card relative flex items-center gap-2.5 rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface p-3 shadow-ga-soft ${
        editing ? 'module-card-editing cursor-grab select-none' : 'cursor-pointer transition-colors hover:border-ga-primary/40 hover:bg-ga-bg-surface-subtle/40'
      }`}
      {...(editing ? { ...attributes, ...listeners } : {})}
      role="button"
      tabIndex={editing ? undefined : 0}
      aria-label={editing ? `${module.title} (drag to reorder)` : module.title}
      onClick={editing ? undefined : onNavigate}
      onKeyDown={
        editing
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate();
              }
            }
      }
      onPointerDown={startPress}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onPointerMove={cancelPress}
      onContextMenu={editing ? (e) => e.preventDefault() : undefined}
    >
      {editing && (
        <button
          type="button"
          className="absolute -top-2 -left-2 z-10 flex h-6 w-6 items-center justify-center rounded-ga-full bg-ga-text-muted text-ga-text-on-primary shadow-ga-soft hover:bg-ga-danger"
          aria-label={`Hide ${module.title}`}
          onClick={(e) => {
            e.stopPropagation();
            onHide();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      <ModuleCardBody module={module} badge={badge} />
      {editing && (
        <GripVertical
          className="ml-auto h-4 w-4 shrink-0 text-ga-text-subtle"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

function HiddenRow({ hiddenIds, onRestore }) {
  if (hiddenIds.length === 0) return null;
  return (
    <div className="mt-3 rounded-ga-md border border-dashed border-ga-border-subtle p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ga-text-subtle">Hidden</p>
      <div className="flex flex-wrap gap-2">
        {hiddenIds.map((id) => {
          const module = MODULE_REGISTRY[id];
          const Icon = module.icon;
          return (
            <button
              key={id}
              type="button"
              className="flex items-center gap-1.5 rounded-ga-full border border-ga-border-subtle bg-ga-bg-surface-subtle/40 px-3 py-1.5 text-xs font-medium text-ga-text-primary hover:border-ga-primary/40 hover:bg-ga-primary-soft hover:text-ga-primary"
              aria-label={`Show ${module.title}`}
              onClick={() => onRestore(id)}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {module.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-14 animate-pulse rounded-ga-md bg-ga-bg-surface-subtle"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

const announcements = {
  onDragStart({ active }) {
    return `Picked up ${MODULE_REGISTRY[active.id]?.title || active.id}.`;
  },
  onDragOver({ active, over }) {
    if (over) {
      return `${MODULE_REGISTRY[active.id]?.title} is over ${MODULE_REGISTRY[over.id]?.title}.`;
    }
    return undefined;
  },
  onDragEnd({ active, over }) {
    if (over) {
      return `${MODULE_REGISTRY[active.id]?.title} dropped on ${MODULE_REGISTRY[over.id]?.title}.`;
    }
    return `${MODULE_REGISTRY[active.id]?.title} dropped.`;
  },
  onDragCancel({ active }) {
    return `Dragging ${MODULE_REGISTRY[active.id]?.title} cancelled.`;
  },
};

export default function ModuleGrid({ badges = {} }) {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { layout, update } = useDashboardLayout(user?.id, user?.role_name, hasPermission);
  const [editing, setEditing] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Exit edit mode automatically on route change.
  useEffect(() => {
    setEditing(false);
  }, [location.pathname]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!user) return null;
  if (!layout) {
    return (
      <section aria-label="Modules">
        <GridSkeleton />
      </section>
    );
  }

  const visible = layout.order.filter((id) => !layout.hidden.includes(id));

  const onDragStart = ({ active }) => setActiveId(active.id);
  const onDragCancel = () => setActiveId(null);
  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (over && active.id !== over.id) {
      update({
        ...layout,
        order: arrayMove(layout.order, layout.order.indexOf(active.id), layout.order.indexOf(over.id)),
      });
    }
  };

  const activeModule = activeId ? MODULE_REGISTRY[activeId] : null;

  return (
    <section aria-label="Modules">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ga-text-subtle">Modules</h2>
        <button
          type="button"
          className={`flex items-center gap-1.5 rounded-ga-full px-3 py-1.5 text-xs font-semibold transition-colors ${
            editing
              ? 'bg-ga-primary text-ga-text-on-primary hover:opacity-90'
              : 'border border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary hover:bg-ga-bg-surface-subtle'
          }`}
          onClick={() => setEditing((e) => !e)}
        >
          {editing ? (
            <>
              <Check className="h-3.5 w-3.5" aria-hidden="true" /> Done
            </>
          ) : (
            <>
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
            </>
          )}
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
        accessibility={{ announcements }}
      >
        <SortableContext items={visible} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {visible.map((id) => {
              const module = MODULE_REGISTRY[id];
              return (
                <SortableModuleCard
                  key={id}
                  module={module}
                  editing={editing}
                  badge={module.badgeKey ? badges[module.badgeKey] || 0 : 0}
                  onHide={() => update({ ...layout, hidden: [...layout.hidden, id] })}
                  onNavigate={() => navigate(module.route)}
                  onLongPress={() => setEditing(true)}
                />
              );
            })}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeModule ? (
            <div className="flex items-center gap-2.5 rounded-ga-md border border-ga-primary bg-ga-bg-surface p-3 shadow-ga-soft">
              <ModuleCardBody module={activeModule} badge={0} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {editing && (
        <HiddenRow
          hiddenIds={layout.hidden}
          onRestore={(id) => update({ ...layout, hidden: layout.hidden.filter((h) => h !== id) })}
        />
      )}
    </section>
  );
}
